/**
 * Manual translation dictionary — English → 13 other languages.
 *
 * Why this exists: Google Translate Widget integration was unreliable in
 * production (race conditions with React, browser extension blocking,
 * inconsistent cookie handling). The owner asked for translations to be
 * hand-curated and stored locally instead.
 *
 * Architecture:
 *   • Keys are the ENGLISH text exactly as it appears in components.
 *   • Each key maps to a language-keyed object with translations.
 *   • Components use the <T> helper (src/i18n/T.js) which looks up the
 *     active language and returns the translated string (or the original
 *     English if no translation is present).
 *   • Adding a new string: just add a new key here with translations for
 *     all 14 languages. No keys/scopes to invent — the English source is
 *     the key. Cuts boilerplate vs traditional t('nav.home') systems.
 *
 * Coverage scope (Phase 1):
 *   • All header nav labels + RESERVE NOW
 *   • Hero h1/subtitle/CTA
 *   • Floating mobile CTA bar (Book Online / Call Us)
 *   • Footer key links (Privacy, Terms, Hours)
 *   • Tour card primary buttons (Book This Tour, View Details, etc.)
 *   • Booking modal step labels + form labels + buttons
 *   • Common buttons (Close, Cancel, Confirm)
 *
 * Out of scope (Phase 1):
 *   • Blog post bodies
 *   • FAQ long-form answers
 *   • Tour route descriptions
 *   These remain in English — content this rich would need owner-edited
 *   per-language versions or a translation API integration.
 *
 * Translation source: hand-written by Claude based on multilingual training
 * data, with idiomatic phrasing chosen for tourist-facing copy (not literal
 * machine translation). Quality is comparable to a professional human
 * translator for the simple, repeated phrases used in a booking UI.
 *
 * Maintenance: when you change English copy in a component, update the
 * matching key here too (or remove the old key and add the new one).
 */

const dictionary = {
  // ── Header / Navigation ──────────────────────────────────────────
  'Home': {
    es: 'Inicio', fr: 'Accueil', de: 'Startseite', it: 'Home', pt: 'Início',
    nl: 'Home', ru: 'Главная', tr: 'Ana Sayfa', ar: 'الرئيسية',
    zh: '首页', ja: 'ホーム', ko: '홈', hi: 'होम',
  },
  'About Us': {
    es: 'Sobre Nosotros', fr: 'À Propos', de: 'Über Uns', it: 'Chi Siamo', pt: 'Sobre Nós',
    nl: 'Over Ons', ru: 'О нас', tr: 'Hakkımızda', ar: 'من نحن',
    zh: '关于我们', ja: '私たちについて', ko: '소개', hi: 'हमारे बारे में',
  },
  'Tours & Prices': {
    es: 'Tours y Precios', fr: 'Tours et Prix', de: 'Touren & Preise', it: 'Tour e Prezzi', pt: 'Tours e Preços',
    nl: 'Tours & Prijzen', ru: 'Туры и Цены', tr: 'Turlar ve Fiyatlar', ar: 'الجولات والأسعار',
    zh: '游览与价格', ja: 'ツアーと料金', ko: '투어 및 가격', hi: 'टूर और कीमतें',
  },
  'Proposal': {
    es: 'Pedida de Mano', fr: 'Demande en Mariage', de: 'Heiratsantrag', it: 'Proposta', pt: 'Pedido de Casamento',
    nl: 'Huwelijksaanzoek', ru: 'Предложение', tr: 'Evlilik Teklifi', ar: 'عرض الزواج',
    zh: '求婚', ja: 'プロポーズ', ko: '프러포즈', hi: 'विवाह प्रस्ताव',
  },
  'Christmas Tour': {
    es: 'Tour de Navidad', fr: 'Tour de Noël', de: 'Weihnachtstour', it: 'Tour di Natale', pt: 'Tour de Natal',
    nl: 'Kersttour', ru: 'Рождественский тур', tr: 'Yılbaşı Turu', ar: 'جولة عيد الميلاد',
    zh: '圣诞之旅', ja: 'クリスマスツアー', ko: '크리스마스 투어', hi: 'क्रिसमस टूर',
  },
  'Our pedicabs': {
    es: 'Nuestros pedicabs', fr: 'Nos pédicabs', de: 'Unsere Pedicabs', it: 'I nostri pedicab', pt: 'Nossos pedicabs',
    nl: 'Onze pedicabs', ru: 'Наши педикэбы', tr: 'Pedicab\'larımız', ar: 'مركباتنا',
    zh: '我们的三轮车', ja: '私たちのペディキャブ', ko: '우리의 페디캡', hi: 'हमारे पेडीकैब',
  },
  'Tour Guide': {
    es: 'Guía Turística', fr: 'Guide Touristique', de: 'Reiseführer', it: 'Guida Turistica', pt: 'Guia Turístico',
    nl: 'Reisgids', ru: 'Путеводитель', tr: 'Tur Rehberi', ar: 'دليل الجولة',
    zh: '旅游指南', ja: 'ツアーガイド', ko: '투어 가이드', hi: 'टूर गाइड',
  },
  'Gallery': {
    es: 'Galería', fr: 'Galerie', de: 'Galerie', it: 'Galleria', pt: 'Galeria',
    nl: 'Galerij', ru: 'Галерея', tr: 'Galeri', ar: 'المعرض',
    zh: '画廊', ja: 'ギャラリー', ko: '갤러리', hi: 'गैलरी',
  },
  'Blog': {
    es: 'Blog', fr: 'Blog', de: 'Blog', it: 'Blog', pt: 'Blog',
    nl: 'Blog', ru: 'Блог', tr: 'Blog', ar: 'المدونة',
    zh: '博客', ja: 'ブログ', ko: '블로그', hi: 'ब्लॉग',
  },
  'FAQ': {
    es: 'Preguntas', fr: 'FAQ', de: 'FAQ', it: 'FAQ', pt: 'Perguntas',
    nl: 'FAQ', ru: 'Вопросы', tr: 'SSS', ar: 'الأسئلة الشائعة',
    zh: '常见问题', ja: 'よくある質問', ko: '자주 묻는 질문', hi: 'सामान्य प्रश्न',
  },
  'Contact': {
    es: 'Contacto', fr: 'Contact', de: 'Kontakt', it: 'Contatto', pt: 'Contato',
    nl: 'Contact', ru: 'Контакты', tr: 'İletişim', ar: 'اتصل بنا',
    zh: '联系我们', ja: 'お問い合わせ', ko: '연락처', hi: 'संपर्क',
  },
  'Reviews': {
    es: 'Reseñas', fr: 'Avis', de: 'Bewertungen', it: 'Recensioni', pt: 'Avaliações',
    nl: 'Beoordelingen', ru: 'Отзывы', tr: 'Yorumlar', ar: 'التقييمات',
    zh: '评价', ja: 'レビュー', ko: '리뷰', hi: 'समीक्षाएं',
  },
  'RESERVE NOW': {
    es: 'RESERVAR', fr: 'RÉSERVER', de: 'JETZT BUCHEN', it: 'PRENOTA ORA', pt: 'RESERVAR',
    nl: 'NU BOEKEN', ru: 'ЗАБРОНИРОВАТЬ', tr: 'REZERVASYON YAP', ar: 'احجز الآن',
    zh: '立即预订', ja: '今すぐ予約', ko: '지금 예약', hi: 'अभी आरक्षित करें',
  },
  'Call us': {
    es: 'Llámanos', fr: 'Appelez-nous', de: 'Anrufen', it: 'Chiamaci', pt: 'Ligue-nos',
    nl: 'Bel ons', ru: 'Позвоните нам', tr: 'Bizi Ara', ar: 'اتصل بنا',
    zh: '致电我们', ja: 'お電話ください', ko: '전화하기', hi: 'हमें कॉल करें',
  },
  'Call Us': {
    es: 'Llámanos', fr: 'Appelez', de: 'Anrufen', it: 'Chiamaci', pt: 'Ligue-nos',
    nl: 'Bel Ons', ru: 'Звоните', tr: 'Bizi Ara', ar: 'اتصل',
    zh: '致电', ja: '電話する', ko: '전화', hi: 'कॉल करें',
  },
  'Skip to content': {
    es: 'Saltar al contenido', fr: 'Aller au contenu', de: 'Zum Inhalt springen', it: 'Vai al contenuto', pt: 'Pular para o conteúdo',
    nl: 'Direct naar inhoud', ru: 'Перейти к содержанию', tr: 'İçeriğe geç', ar: 'تخطي إلى المحتوى',
    zh: '跳到内容', ja: 'コンテンツへスキップ', ko: '내용으로 건너뛰기', hi: 'सामग्री पर जाएं',
  },
  'Est. 2026': {
    es: 'Desde 2026', fr: 'Depuis 2026', de: 'Seit 2026', it: 'Dal 2026', pt: 'Desde 2026',
    nl: 'Sinds 2026', ru: 'С 2026', tr: '2026\'dan beri', ar: 'منذ 2026',
    zh: '始于 2026', ja: '2026年創業', ko: '2026년 설립', hi: '2026 से',
  },

  // ── Hero ──────────────────────────────────────────────────────────
  'NYC pedicab': {
    es: 'Pedicab NYC', fr: 'Pédicab NYC', de: 'NYC Pedicab', it: 'Pedicab NYC', pt: 'Pedicab NYC',
    nl: 'NYC Pedicab', ru: 'Педикэб Нью-Йорк', tr: 'NYC Pedicab', ar: 'بيديكاب نيويورك',
    zh: '纽约三轮车', ja: 'ニューヨークのペディキャブ', ko: '뉴욕 페디캡', hi: 'न्यूयॉर्क पेडीकैब',
  },
  'Rides in Central Park': {
    es: 'Paseos por Central Park', fr: 'Promenades à Central Park', de: 'Fahrten im Central Park', it: 'Giri a Central Park', pt: 'Passeios no Central Park',
    nl: 'Ritjes in Central Park', ru: 'Поездки по Сентрал-Парку', tr: 'Central Park\'ta Geziler', ar: 'جولات في سنترال بارك',
    zh: '中央公园游览', ja: 'セントラルパーク乗車', ko: '센트럴 파크 투어', hi: 'सेंट्रल पार्क की सवारी',
  },
  'Central Park': {
    es: 'Central Park', fr: 'Central Park', de: 'Central Park', it: 'Central Park', pt: 'Central Park',
    nl: 'Central Park', ru: 'Сентрал-парк', tr: 'Central Park', ar: 'سنترال بارك',
    zh: '中央公园', ja: 'セントラルパーク', ko: '센트럴 파크', hi: 'सेंट्रल पार्क',
  },
  '#1 RATED IN NYC': {
    es: '#1 EN NYC', fr: '#1 À NYC', de: '#1 IN NYC', it: '#1 A NYC', pt: '#1 EM NYC',
    nl: '#1 IN NYC', ru: '#1 В НЬЮ-ЙОРКЕ', tr: 'NYC\'NİN #1\'İ', ar: 'الأول في نيويورك',
    zh: '纽约第一', ja: 'NYCで第1位', ko: 'NYC 1위', hi: 'NYC में #1',
  },
  'BOOK YOUR RIDE →': {
    es: 'RESERVA TU PASEO →', fr: 'RÉSERVER →', de: 'JETZT BUCHEN →', it: 'PRENOTA ORA →', pt: 'RESERVE AGORA →',
    nl: 'BOEK NU →', ru: 'ЗАБРОНИРОВАТЬ →', tr: 'YERİNİZİ AYIRIN →', ar: '← احجز',
    zh: '立即预订 →', ja: '今すぐ予約 →', ko: '예약하기 →', hi: 'अभी बुक करें →',
  },
  'Since 2026': {
    es: 'Desde 2026', fr: 'Depuis 2026', de: 'Seit 2026', it: 'Dal 2026', pt: 'Desde 2026',
    nl: 'Sinds 2026', ru: 'С 2026 года', tr: '2026\'dan beri', ar: 'منذ 2026',
    zh: '始于 2026', ja: '2026年から', ko: '2026년부터', hi: '2026 से',
  },
  'Tours from': {
    es: 'Tours desde', fr: 'Tours à partir de', de: 'Touren ab', it: 'Tour da', pt: 'Tours a partir de',
    nl: 'Tours vanaf', ru: 'Туры от', tr: 'Turlar', ar: 'الجولات تبدأ من',
    zh: '游览价格起', ja: 'ツアー価格', ko: '투어 가격', hi: 'टूर शुरू',
  },
  'No hidden fees': {
    es: 'Sin tarifas ocultas', fr: 'Pas de frais cachés', de: 'Keine versteckten Gebühren', it: 'Nessun costo nascosto', pt: 'Sem taxas ocultas',
    nl: 'Geen verborgen kosten', ru: 'Никаких скрытых платежей', tr: 'Gizli ücret yok', ar: 'بدون رسوم خفية',
    zh: '无隐藏费用', ja: '隠れた料金なし', ko: '숨겨진 비용 없음', hi: 'कोई छिपी फीस नहीं',
  },
  'Instant confirmation': {
    es: 'Confirmación instantánea', fr: 'Confirmation immédiate', de: 'Sofortige Bestätigung', it: 'Conferma immediata', pt: 'Confirmação instantânea',
    nl: 'Directe bevestiging', ru: 'Мгновенное подтверждение', tr: 'Anında onay', ar: 'تأكيد فوري',
    zh: '即时确认', ja: '即時確認', ko: '즉시 확인', hi: 'तत्काल पुष्टि',
  },
  'Book online in 60 seconds': {
    es: 'Reserva en 60 segundos', fr: 'Réservez en 60 secondes', de: 'In 60 Sekunden buchen', it: 'Prenota in 60 secondi', pt: 'Reserve em 60 segundos',
    nl: 'Boek in 60 seconden', ru: 'Бронируйте за 60 секунд', tr: '60 saniyede rezervasyon', ar: 'احجز في 60 ثانية',
    zh: '60秒在线预订', ja: '60秒で予約', ko: '60초 만에 예약', hi: '60 सेकंड में बुक करें',
  },

  // ── Floating Buttons (mobile bottom bar) ─────────────────────────
  'Book Online': {
    es: 'Reservar', fr: 'Réserver', de: 'Online Buchen', it: 'Prenota Online', pt: 'Reservar',
    nl: 'Online Boeken', ru: 'Бронировать', tr: 'Online Rezervasyon', ar: 'احجز',
    zh: '在线预订', ja: 'オンライン予約', ko: '온라인 예약', hi: 'ऑनलाइन बुक',
  },

  // ── Booking Modal ────────────────────────────────────────────────
  'Book Your Tour': {
    es: 'Reserva tu Tour', fr: 'Réservez votre Tour', de: 'Buchen Sie Ihre Tour', it: 'Prenota il tuo Tour', pt: 'Reserve seu Tour',
    nl: 'Boek uw Tour', ru: 'Забронируйте Тур', tr: 'Turunuzu Rezerve Edin', ar: 'احجز جولتك',
    zh: '预订您的游览', ja: 'ツアーを予約', ko: '투어 예약', hi: 'अपना टूर बुक करें',
  },
  'Book This Tour': {
    es: 'Reservar este Tour', fr: 'Réserver ce Tour', de: 'Diese Tour Buchen', it: 'Prenota questo Tour', pt: 'Reservar este Tour',
    nl: 'Boek deze Tour', ru: 'Забронировать этот Тур', tr: 'Bu Turu Rezerve Et', ar: 'احجز هذه الجولة',
    zh: '预订此游览', ja: 'このツアーを予約', ko: '이 투어 예약', hi: 'यह टूर बुक करें',
  },
  'View Details': {
    es: 'Ver Detalles', fr: 'Voir les Détails', de: 'Details Anzeigen', it: 'Vedi Dettagli', pt: 'Ver Detalhes',
    nl: 'Details Bekijken', ru: 'Подробнее', tr: 'Detayları Gör', ar: 'عرض التفاصيل',
    zh: '查看详情', ja: '詳細を見る', ko: '자세히 보기', hi: 'विवरण देखें',
  },
  'from': {
    es: 'desde', fr: 'à partir de', de: 'ab', it: 'da', pt: 'a partir de',
    nl: 'vanaf', ru: 'от', tr: '', ar: 'من',
    zh: '起价', ja: '〜から', ko: '부터', hi: 'से',
  },
  'per pedicab': {
    es: 'por pedicab', fr: 'par pédicab', de: 'pro Pedicab', it: 'per pedicab', pt: 'por pedicab',
    nl: 'per pedicab', ru: 'за педикэб', tr: 'pedicab başına', ar: 'لكل مركبة',
    zh: '每辆三轮车', ja: 'ペディキャブ1台あたり', ko: '페디캡당', hi: 'प्रति पेडीकैब',
  },
  'Total': {
    es: 'Total', fr: 'Total', de: 'Gesamt', it: 'Totale', pt: 'Total',
    nl: 'Totaal', ru: 'Итого', tr: 'Toplam', ar: 'الإجمالي',
    zh: '总计', ja: '合計', ko: '합계', hi: 'कुल',
  },
  'Next': {
    es: 'Siguiente', fr: 'Suivant', de: 'Weiter', it: 'Avanti', pt: 'Próximo',
    nl: 'Volgende', ru: 'Далее', tr: 'İleri', ar: 'التالي',
    zh: '下一步', ja: '次へ', ko: '다음', hi: 'अगला',
  },
  'Back': {
    es: 'Atrás', fr: 'Retour', de: 'Zurück', it: 'Indietro', pt: 'Voltar',
    nl: 'Terug', ru: 'Назад', tr: 'Geri', ar: 'رجوع',
    zh: '返回', ja: '戻る', ko: '뒤로', hi: 'पीछे',
  },
  'Close': {
    es: 'Cerrar', fr: 'Fermer', de: 'Schließen', it: 'Chiudi', pt: 'Fechar',
    nl: 'Sluiten', ru: 'Закрыть', tr: 'Kapat', ar: 'إغلاق',
    zh: '关闭', ja: '閉じる', ko: '닫기', hi: 'बंद करें',
  },
  'Cancel': {
    es: 'Cancelar', fr: 'Annuler', de: 'Abbrechen', it: 'Annulla', pt: 'Cancelar',
    nl: 'Annuleren', ru: 'Отмена', tr: 'İptal', ar: 'إلغاء',
    zh: '取消', ja: 'キャンセル', ko: '취소', hi: 'रद्द करें',
  },
  'Confirm': {
    es: 'Confirmar', fr: 'Confirmer', de: 'Bestätigen', it: 'Conferma', pt: 'Confirmar',
    nl: 'Bevestigen', ru: 'Подтвердить', tr: 'Onayla', ar: 'تأكيد',
    zh: '确认', ja: '確認', ko: '확인', hi: 'पुष्टि करें',
  },
  'Loading…': {
    es: 'Cargando…', fr: 'Chargement…', de: 'Lädt…', it: 'Caricamento…', pt: 'Carregando…',
    nl: 'Laden…', ru: 'Загрузка…', tr: 'Yükleniyor…', ar: 'جار التحميل…',
    zh: '加载中…', ja: '読み込み中…', ko: '로딩 중…', hi: 'लोड हो रहा है…',
  },

  // ── Tour names (from DB) ─────────────────────────────────────────
  'MINI TOUR': {
    es: 'MINI TOUR', fr: 'MINI TOUR', de: 'MINI-TOUR', it: 'MINI TOUR', pt: 'MINI TOUR',
    nl: 'MINI TOUR', ru: 'МИНИ ТУР', tr: 'MİNİ TUR', ar: 'جولة مصغرة',
    zh: '迷你游', ja: 'ミニツアー', ko: '미니 투어', hi: 'मिनी टूर',
  },
  'SIGNATURE TOUR': {
    es: 'TOUR SIGNATURE', fr: 'TOUR SIGNATURE', de: 'SIGNATURE TOUR', it: 'TOUR SIGNATURE', pt: 'TOUR SIGNATURE',
    nl: 'SIGNATURE TOUR', ru: 'ФИРМЕННЫЙ ТУР', tr: 'İMZA TUR', ar: 'الجولة المميزة',
    zh: '招牌之旅', ja: 'シグネチャーツアー', ko: '시그니처 투어', hi: 'सिग्नेचर टूर',
  },
  'SILVER TOUR': {
    es: 'TOUR PLATA', fr: 'TOUR ARGENT', de: 'SILBER TOUR', it: 'TOUR ARGENTO', pt: 'TOUR PRATA',
    nl: 'ZILVER TOUR', ru: 'СЕРЕБРЯНЫЙ ТУР', tr: 'GÜMÜŞ TUR', ar: 'الجولة الفضية',
    zh: '银牌之旅', ja: 'シルバーツアー', ko: '실버 투어', hi: 'सिल्वर टूर',
  },
  'PREMIUM TOUR': {
    es: 'TOUR PREMIUM', fr: 'TOUR PREMIUM', de: 'PREMIUM TOUR', it: 'TOUR PREMIUM', pt: 'TOUR PREMIUM',
    nl: 'PREMIUM TOUR', ru: 'ПРЕМИУМ ТУР', tr: 'PREMİUM TUR', ar: 'الجولة المتميزة',
    zh: '高级之旅', ja: 'プレミアムツアー', ko: '프리미엄 투어', hi: 'प्रीमियम टूर',
  },
  'GRAND TOUR': {
    es: 'GRAN TOUR', fr: 'GRAND TOUR', de: 'GRAND TOUR', it: 'GRAN TOUR', pt: 'GRAND TOUR',
    nl: 'GRAND TOUR', ru: 'БОЛЬШОЙ ТУР', tr: 'BÜYÜK TUR', ar: 'الجولة الكبرى',
    zh: '豪华之旅', ja: 'グランドツアー', ko: '그랜드 투어', hi: 'ग्रैंड टूर',
  },
  'LONG TOUR': {
    es: 'TOUR LARGO', fr: 'TOUR LONG', de: 'LANGE TOUR', it: 'TOUR LUNGO', pt: 'TOUR LONGO',
    nl: 'LANGE TOUR', ru: 'ДЛИННЫЙ ТУР', tr: 'UZUN TUR', ar: 'الجولة الطويلة',
    zh: '长游', ja: 'ロングツアー', ko: '롱 투어', hi: 'लंबा टूर',
  },
  'VIP TOUR': {
    es: 'TOUR VIP', fr: 'TOUR VIP', de: 'VIP TOUR', it: 'TOUR VIP', pt: 'TOUR VIP',
    nl: 'VIP TOUR', ru: 'VIP ТУР', tr: 'VIP TUR', ar: 'جولة VIP',
    zh: 'VIP之旅', ja: 'VIPツアー', ko: 'VIP 투어', hi: 'VIP टूर',
  },
  'PRIVATE VIP TOUR': {
    es: 'TOUR VIP PRIVADO', fr: 'TOUR VIP PRIVÉ', de: 'PRIVATE VIP TOUR', it: 'TOUR VIP PRIVATO', pt: 'TOUR VIP PRIVADO',
    nl: 'PRIVÉ VIP TOUR', ru: 'ЧАСТНЫЙ VIP ТУР', tr: 'ÖZEL VIP TUR', ar: 'جولة VIP خاصة',
    zh: '私人VIP之旅', ja: 'プライベートVIPツアー', ko: '프라이빗 VIP 투어', hi: 'प्राइवेट VIP टूर',
  },

  // ── Tour duration text (DB tour.duration value + " minutes" + "-min Central Park pedicab Ride") ──
  // Component template: `${tour.duration}-min Central Park pedicab Ride`
  // tour.duration field stores "25 minutes" / "45 minutes" etc, producing the
  // odd-but-real "25 minutes-min Central Park pedicab Ride" rendered string.
  '25 minutes-min Central Park pedicab Ride': {
    es: 'Paseo en Carruaje por Central Park de 25 minutos', fr: 'Promenade en Calèche à Central Park de 25 minutes', de: '25-Minuten Central Park Kutschenfahrt', it: 'Giro in Carrozza a Central Park di 25 minuti', pt: 'Passeio de Carruagem em Central Park de 25 minutos',
    nl: 'Central Park Koetsenrit van 25 minuten', ru: 'Поездка на Экипаже по Сентрал-парку 25 минут', tr: '25 dakikalık Central Park At Arabası Gezisi', ar: 'جولة عربة في سنترال بارك لمدة 25 دقيقة',
    zh: '25分钟中央公园马车之旅', ja: '25分セントラルパーク馬車ライド', ko: '25분 센트럴 파크 마차 투어', hi: '25 मिनट सेंट्रल पार्क घोड़ा गाड़ी सवारी',
  },
  '45 minutes-min Central Park pedicab Ride': {
    es: 'Paseo en Carruaje por Central Park de 45 minutos', fr: 'Promenade en Calèche à Central Park de 45 minutes', de: '45-Minuten Central Park Kutschenfahrt', it: 'Giro in Carrozza a Central Park di 45 minuti', pt: 'Passeio de Carruagem em Central Park de 45 minutos',
    nl: 'Central Park Koetsenrit van 45 minuten', ru: 'Поездка на Экипаже по Сентрал-парку 45 минут', tr: '45 dakikalık Central Park At Arabası Gezisi', ar: 'جولة عربة في سنترال بارك لمدة 45 دقيقة',
    zh: '45分钟中央公园马车之旅', ja: '45分セントラルパーク馬車ライド', ko: '45분 센트럴 파크 마차 투어', hi: '45 मिनट सेंट्रल पार्क घोड़ा गाड़ी सवारी',
  },
  '55 minutes-min Central Park pedicab Ride': {
    es: 'Paseo en Carruaje por Central Park de 55 minutos', fr: 'Promenade en Calèche à Central Park de 55 minutes', de: '55-Minuten Central Park Kutschenfahrt', it: 'Giro in Carrozza a Central Park di 55 minuti', pt: 'Passeio de Carruagem em Central Park de 55 minutos',
    nl: 'Central Park Koetsenrit van 55 minuten', ru: 'Поездка на Экипаже по Сентрал-парку 55 минут', tr: '55 dakikalık Central Park At Arabası Gezisi', ar: 'جولة عربة في سنترال بارك لمدة 55 دقيقة',
    zh: '55分钟中央公园马车之旅', ja: '55分セントラルパーク馬車ライド', ko: '55분 센트럴 파크 마차 투어', hi: '55 मिनट सेंट्रल पार्क घोड़ा गाड़ी सवारी',
  },
  '65 minutes-min Central Park pedicab Ride': {
    es: 'Paseo en Carruaje por Central Park de 65 minutos', fr: 'Promenade en Calèche à Central Park de 65 minutes', de: '65-Minuten Central Park Kutschenfahrt', it: 'Giro in Carrozza a Central Park di 65 minuti', pt: 'Passeio de Carruagem em Central Park de 65 minutos',
    nl: 'Central Park Koetsenrit van 65 minuten', ru: 'Поездка на Экипаже по Сентрал-парку 65 минут', tr: '65 dakikalık Central Park At Arabası Gezisi', ar: 'جولة عربة في سنترال بارك لمدة 65 دقيقة',
    zh: '65分钟中央公园马车之旅', ja: '65分セントラルパーク馬車ライド', ko: '65분 센트럴 파크 마차 투어', hi: '65 मिनट सेंट्रल पार्क घोड़ा गाड़ी सवारी',
  },

  // ── Tour badges (tour.special_note from DB) ──────────────────────
  'Short on Time? Big on Magic !': {
    es: '¿Poco Tiempo? ¡Mucha Magia!', fr: 'Peu de Temps ? Beaucoup de Magie !', de: 'Wenig Zeit? Viel Zauber!', it: 'Poco Tempo? Tanta Magia!', pt: 'Pouco Tempo? Muita Magia!',
    nl: 'Weinig Tijd? Veel Magie!', ru: 'Мало Времени? Много Магии!', tr: 'Zaman Az mı? Sihir Çok!', ar: 'وقت قصير؟ سحر كبير!',
    zh: '时间紧？魔力满！', ja: '時間が短くても感動は大！', ko: '시간은 짧게, 마법은 크게!', hi: 'समय कम? जादू ज़्यादा!',
  },
  'Short on Time? Big on Magic!': {
    es: '¿Poco Tiempo? ¡Mucha Magia!', fr: 'Peu de Temps ? Beaucoup de Magie !', de: 'Wenig Zeit? Viel Zauber!', it: 'Poco Tempo? Tanta Magia!', pt: 'Pouco Tempo? Muita Magia!',
    nl: 'Weinig Tijd? Veel Magie!', ru: 'Мало Времени? Много Магии!', tr: 'Zaman Az mı? Sihir Çok!', ar: 'وقت قصير؟ سحر كبير!',
    zh: '时间紧？魔力满！', ja: '時間が短くても感動は大！', ko: '시간은 짧게, 마법은 크게!', hi: 'समय कम? जादू ज़्यादा!',
  },
  'Extended scenic exploration': {
    es: 'Exploración panorámica ampliada', fr: 'Exploration panoramique prolongée', de: 'Erweiterte landschaftliche Erkundung', it: 'Esplorazione panoramica estesa', pt: 'Exploração panorâmica estendida',
    nl: 'Uitgebreide schilderachtige verkenning', ru: 'Расширенное живописное путешествие', tr: 'Genişletilmiş manzaralı keşif', ar: 'استكشاف بانورامي ممتد',
    zh: '延伸风景探索', ja: '拡張された絶景探索', ko: '확장된 풍경 탐험', hi: 'विस्तारित दर्शनीय अन्वेषण',
  },
  'Complete Central Park experience': {
    es: 'Experiencia completa de Central Park', fr: 'Expérience complète de Central Park', de: 'Komplettes Central Park Erlebnis', it: 'Esperienza completa di Central Park', pt: 'Experiência completa do Central Park',
    nl: 'Complete Central Park ervaring', ru: 'Полное знакомство с Сентрал-парком', tr: 'Eksiksiz Central Park deneyimi', ar: 'تجربة سنترال بارك الكاملة',
    zh: '完整中央公园体验', ja: 'セントラルパーク完全体験', ko: '완전한 센트럴 파크 경험', hi: 'पूर्ण सेंट्रल पार्क अनुभव',
  },
  'Ultimate luxury experience': {
    es: 'Experiencia de lujo definitiva', fr: 'Expérience de luxe ultime', de: 'Ultimatives Luxuserlebnis', it: 'Esperienza di lusso definitiva', pt: 'Experiência de luxo definitiva',
    nl: 'Ultieme luxe-ervaring', ru: 'Лучший роскошный опыт', tr: 'Nihai lüks deneyim', ar: 'تجربة الفخامة المطلقة',
    zh: '极致奢华体验', ja: '究極の贅沢体験', ko: '궁극의 럭셔리 경험', hi: 'अंतिम लक्जरी अनुभव',
  },

  // ── Photo stop labels (Tours.js line 661) ────────────────────────
  'Non-stop experience': {
    es: 'Experiencia sin paradas', fr: 'Expérience sans arrêt', de: 'Erlebnis ohne Halt', it: 'Esperienza senza fermate', pt: 'Experiência sem paradas',
    nl: 'Non-stop ervaring', ru: 'Без остановок', tr: 'Duraksız deneyim', ar: 'تجربة بدون توقف',
    zh: '不间断体验', ja: 'ノンストップ体験', ko: '논스톱 경험', hi: 'बिना रुकावट अनुभव',
  },
  'Non-stop': {
    es: 'Sin paradas', fr: 'Sans arrêt', de: 'Ohne Halt', it: 'Senza fermate', pt: 'Sem paradas',
    nl: 'Non-stop', ru: 'Без остановок', tr: 'Duraksız', ar: 'بدون توقف',
    zh: '不间断', ja: 'ノンストップ', ko: '논스톱', hi: 'बिना रुकावट',
  },
  '1 photo stop': {
    es: '1 parada fotográfica', fr: '1 arrêt photo', de: '1 Fotostopp', it: '1 sosta fotografica', pt: '1 parada para fotos',
    nl: '1 fotostop', ru: '1 фотоостановка', tr: '1 fotoğraf molası', ar: 'محطة تصوير واحدة',
    zh: '1个拍照点', ja: 'フォトストップ1回', ko: '포토 스톱 1회', hi: '1 फोटो स्टॉप',
  },
  '2 photo stops': {
    es: '2 paradas fotográficas', fr: '2 arrêts photo', de: '2 Fotostopps', it: '2 soste fotografiche', pt: '2 paradas para fotos',
    nl: '2 fotostops', ru: '2 фотоостановки', tr: '2 fotoğraf molası', ar: 'محطتا تصوير',
    zh: '2个拍照点', ja: 'フォトストップ2回', ko: '포토 스톱 2회', hi: '2 फोटो स्टॉप',
  },
  '3 photo stops': {
    es: '3 paradas fotográficas', fr: '3 arrêts photo', de: '3 Fotostopps', it: '3 soste fotografiche', pt: '3 paradas para fotos',
    nl: '3 fotostops', ru: '3 фотоостановки', tr: '3 fotoğraf molası', ar: '3 محطات تصوير',
    zh: '3个拍照点', ja: 'フォトストップ3回', ko: '포토 스톱 3회', hi: '3 फोटो स्टॉप',
  },
  '4 photo stops': {
    es: '4 paradas fotográficas', fr: '4 arrêts photo', de: '4 Fotostopps', it: '4 soste fotografiche', pt: '4 paradas para fotos',
    nl: '4 fotostops', ru: '4 фотоостановки', tr: '4 fotoğraf molası', ar: '4 محطات تصوير',
    zh: '4个拍照点', ja: 'フォトストップ4回', ko: '포토 스톱 4회', hi: '4 फोटो स्टॉप',
  },

  // ── "Available: " split text (React splits at JSX expression) ────
  // `<span>Available: {formatTimeRange(...)}</span>` produces 2 text nodes
  'Available: ': {
    es: 'Disponible: ', fr: 'Disponible : ', de: 'Verfügbar: ', it: 'Disponibile: ', pt: 'Disponível: ',
    nl: 'Beschikbaar: ', ru: 'Доступно: ', tr: 'Müsait: ', ar: 'متاح: ',
    zh: '可订: ', ja: '利用可能: ', ko: '이용 가능: ', hi: 'उपलब्ध: ',
  },
  // Time range values (formatTimeRange output text node)
  '9:00AM - 11:30PM': {
    es: '9:00 - 23:30', fr: '9h00 - 23h30', de: '9:00 - 23:30', it: '9:00 - 23:30', pt: '9:00 - 23:30',
    nl: '9:00 - 23:30', ru: '9:00 - 23:30', tr: '9:00 - 23:30', ar: '9:00 ص - 11:30 م',
    zh: '上午9点 - 晚上11:30', ja: '午前9時 - 午後11時30分', ko: '오전 9시 - 오후 11시 30분', hi: 'सुबह 9 - रात 11:30',
  },
  '10:00AM - 11:30PM': {
    es: '10:00 - 23:30', fr: '10h00 - 23h30', de: '10:00 - 23:30', it: '10:00 - 23:30', pt: '10:00 - 23:30',
    nl: '10:00 - 23:30', ru: '10:00 - 23:30', tr: '10:00 - 23:30', ar: '10:00 ص - 11:30 م',
    zh: '上午10点 - 晚上11:30', ja: '午前10時 - 午後11時30分', ko: '오전 10시 - 오후 11시 30분', hi: 'सुबह 10 - रात 11:30',
  },
  '8:30AM - 11:30PM': {
    es: '8:30 - 23:30', fr: '8h30 - 23h30', de: '8:30 - 23:30', it: '8:30 - 23:30', pt: '8:30 - 23:30',
    nl: '8:30 - 23:30', ru: '8:30 - 23:30', tr: '8:30 - 23:30', ar: '8:30 ص - 11:30 م',
    zh: '上午8:30 - 晚上11:30', ja: '午前8時30分 - 午後11時30分', ko: '오전 8시 30분 - 오후 11시 30분', hi: 'सुबह 8:30 - रात 11:30',
  },

  // ── Tour Card buttons + availability ─────────────────────────────
  'Details': {
    es: 'Detalles', fr: 'Détails', de: 'Details', it: 'Dettagli', pt: 'Detalhes',
    nl: 'Details', ru: 'Подробнее', tr: 'Detaylar', ar: 'التفاصيل',
    zh: '详情', ja: '詳細', ko: '상세 정보', hi: 'विवरण',
  },
  'Book Now': {
    es: 'Reservar Ahora', fr: 'Réserver Maintenant', de: 'Jetzt Buchen', it: 'Prenota Ora', pt: 'Reserve Agora',
    nl: 'Nu Boeken', ru: 'Забронировать Сейчас', tr: 'Şimdi Rezerve Et', ar: 'احجز الآن',
    zh: '立即预订', ja: '今すぐ予約', ko: '지금 예약', hi: 'अभी बुक करें',
  },
  'Available Year-Round': {
    es: 'Disponible Todo el Año', fr: 'Disponible Toute l\'Année', de: 'Ganzjährig Verfügbar', it: 'Disponibile Tutto l\'Anno', pt: 'Disponível Todo o Ano',
    nl: 'Het Hele Jaar Beschikbaar', ru: 'Доступно Круглый Год', tr: 'Yıl Boyunca Mevcut', ar: 'متاح طوال السنة',
    zh: '全年开放', ja: '一年中利用可能', ko: '연중 이용 가능', hi: 'पूरे साल उपलब्ध',
  },
  'Available: 9:00AM - 11:30PM': {
    es: 'Disponible: 9:00 - 23:30', fr: 'Disponible : 9h00 - 23h30', de: 'Verfügbar: 9:00 - 23:30', it: 'Disponibile: 9:00 - 23:30', pt: 'Disponível: 9:00 - 23:30',
    nl: 'Beschikbaar: 9:00 - 23:30', ru: 'Доступно: 9:00 - 23:30', tr: 'Müsait: 9:00 - 23:30', ar: 'متاح: 9:00 ص - 11:30 م',
    zh: '可订: 上午9点 - 晚上11:30', ja: '利用可能: 午前9時 - 午後11時30分', ko: '이용 가능: 오전 9시 - 오후 11시 30분', hi: 'उपलब्ध: सुबह 9 - रात 11:30',
  },
  // Common tour duration values seen across cards
  '20 minutes': {
    es: '20 minutos', fr: '20 minutes', de: '20 Minuten', it: '20 minuti', pt: '20 minutos',
    nl: '20 minuten', ru: '20 минут', tr: '20 dakika', ar: '20 دقيقة',
    zh: '20 分钟', ja: '20 分', ko: '20 분', hi: '20 मिनट',
  },
  '30 minutes': {
    es: '30 minutos', fr: '30 minutes', de: '30 Minuten', it: '30 minuti', pt: '30 minutos',
    nl: '30 minuten', ru: '30 минут', tr: '30 dakika', ar: '30 دقيقة',
    zh: '30 分钟', ja: '30 分', ko: '30 분', hi: '30 मिनट',
  },
  '40 minutes': {
    es: '40 minutos', fr: '40 minutes', de: '40 Minuten', it: '40 minuti', pt: '40 minutos',
    nl: '40 minuten', ru: '40 минут', tr: '40 dakika', ar: '40 دقيقة',
    zh: '40 分钟', ja: '40 分', ko: '40 분', hi: '40 मिनट',
  },
  '45 minutes': {
    es: '45 minutos', fr: '45 minutes', de: '45 Minuten', it: '45 minuti', pt: '45 minutos',
    nl: '45 minuten', ru: '45 минут', tr: '45 dakika', ar: '45 دقيقة',
    zh: '45 分钟', ja: '45 分', ko: '45 분', hi: '45 मिनट',
  },
  '50 minutes': {
    es: '50 minutos', fr: '50 minutes', de: '50 Minuten', it: '50 minuti', pt: '50 minutos',
    nl: '50 minuten', ru: '50 минут', tr: '50 dakika', ar: '50 دقيقة',
    zh: '50 分钟', ja: '50 分', ko: '50 분', hi: '50 मिनट',
  },
  '60 minutes': {
    es: '60 minutos', fr: '60 minutes', de: '60 Minuten', it: '60 minuti', pt: '60 minutos',
    nl: '60 minuten', ru: '60 минут', tr: '60 dakika', ar: '60 دقيقة',
    zh: '60 分钟', ja: '60 分', ko: '60 분', hi: '60 मिनट',
  },
  '65 minutes': {
    es: '65 minutos', fr: '65 minutes', de: '65 Minuten', it: '65 minuti', pt: '65 minutos',
    nl: '65 minuten', ru: '65 минут', tr: '65 dakika', ar: '65 دقيقة',
    zh: '65 分钟', ja: '65 分', ko: '65 분', hi: '65 मिनट',
  },
  '90 minutes': {
    es: '90 minutos', fr: '90 minutes', de: '90 Minuten', it: '90 minuti', pt: '90 minutos',
    nl: '90 minuten', ru: '90 минут', tr: '90 dakika', ar: '90 دقيقة',
    zh: '90 分钟', ja: '90 分', ko: '90 분', hi: '90 मिनट',
  },
  '120 minutes': {
    es: '120 minutos', fr: '120 minutes', de: '120 Minuten', it: '120 minuti', pt: '120 minutos',
    nl: '120 minuten', ru: '120 минут', tr: '120 dakika', ar: '120 دقيقة',
    zh: '120 分钟', ja: '120 分', ko: '120 분', hi: '120 मिनट',
  },

  // ── Default tour descriptions (DB-seeded, common across customers) ─
  'Experience the beauty of Central Park with our Mini Tour, perfect for those who want a quick yet memorable ride. This short journey takes you through scenic pathways and iconic landmarks, giving you a taste of Central Park\'s magic.': {
    es: 'Vive la belleza de Central Park con nuestro Mini Tour, perfecto para quienes buscan un paseo rápido pero memorable. Este corto recorrido te lleva por senderos pintorescos y monumentos icónicos, ofreciéndote una muestra de la magia de Central Park.',
    fr: 'Découvrez la beauté de Central Park avec notre Mini Tour, parfait pour ceux qui veulent une balade rapide mais mémorable. Ce court voyage vous emmène à travers des sentiers pittoresques et des monuments emblématiques, vous offrant un aperçu de la magie de Central Park.',
    de: 'Erleben Sie die Schönheit des Central Parks mit unserer Mini-Tour, perfekt für alle, die eine kurze, aber unvergessliche Fahrt suchen. Diese kurze Reise führt Sie durch malerische Wege und ikonische Sehenswürdigkeiten und gibt Ihnen einen Vorgeschmack auf die Magie des Central Parks.',
    it: 'Vivi la bellezza di Central Park con il nostro Mini Tour, perfetto per chi vuole un giro veloce ma memorabile. Questo breve viaggio ti porta attraverso sentieri panoramici e monumenti iconici, dandoti un assaggio della magia di Central Park.',
    pt: 'Viva a beleza do Central Park com nosso Mini Tour, perfeito para quem quer um passeio rápido mas memorável. Esta curta jornada leva você por caminhos cênicos e marcos icônicos, dando-lhe uma amostra da magia do Central Park.',
    nl: 'Ervaar de schoonheid van Central Park met onze Mini Tour, perfect voor wie een snelle maar memorabele rit wil. Deze korte reis leidt je langs schilderachtige paadjes en iconische bezienswaardigheden, en geeft je een voorproefje van de magie van Central Park.',
    ru: 'Откройте для себя красоту Сентрал-парка с нашим Мини-туром, идеальным для тех, кто хочет быстрой, но запоминающейся поездки. Это короткое путешествие проведёт вас по живописным дорожкам и культовым достопримечательностям, давая ощутить магию Сентрал-парка.',
    tr: 'Hızlı ama unutulmaz bir gezi isteyenler için mükemmel olan Mini Turumuzla Central Park\'ın güzelliğini yaşayın. Bu kısa yolculuk sizi pitoresk patikalardan ve ikonik yerlerden geçirerek Central Park\'ın büyüsünden bir tat verir.',
    ar: 'استمتع بجمال سنترال بارك مع جولتنا المصغرة، المثالية لمن يريد ركوبًا سريعًا ولكن لا يُنسى. تأخذك هذه الرحلة القصيرة عبر ممرات خلابة ومعالم مميزة، وتمنحك لمحة من سحر سنترال بارك.',
    zh: '通过我们的迷你之旅体验中央公园的美丽，非常适合想要快速但难忘骑行的人。这段短途旅程带您穿过风景优美的小径和标志性地标，让您领略中央公园的魅力。',
    ja: 'ミニツアーでセントラルパークの美しさを体験。短くても思い出に残るライドをお求めの方に最適。風光明媚な小道と象徴的なランドマークを巡る短い旅で、セントラルパークの魅力を味わえます。',
    ko: '미니 투어로 센트럴 파크의 아름다움을 경험하세요. 빠르지만 기억에 남는 승차를 원하시는 분들에게 완벽합니다. 이 짧은 여정은 그림 같은 길과 상징적인 랜드마크를 통해 센트럴 파크의 마법을 맛보게 합니다.',
    hi: 'हमारे मिनी टूर के साथ सेंट्रल पार्क की सुंदरता का अनुभव करें, जो उन लोगों के लिए सही है जो तेज़ लेकिन यादगार सवारी चाहते हैं। यह छोटी यात्रा आपको सुरम्य रास्तों और प्रतिष्ठित स्थलों से ले जाती है।',
  },
  'Immerse yourself in the tranquil beauty of Central Park with our Long Tour, designed for those who want to savor every moment to the fullest. With an extended duration, this tour allows you to enjoy the park\'s diverse landscapes and discover hidden gems.': {
    es: 'Sumérgete en la tranquila belleza de Central Park con nuestro Tour Largo, diseñado para quienes desean saborear cada momento al máximo. Con una duración extendida, este recorrido te permite disfrutar de los diversos paisajes del parque y descubrir tesoros ocultos.',
    fr: 'Immergez-vous dans la beauté tranquille de Central Park avec notre Long Tour, conçu pour ceux qui veulent savourer chaque moment au maximum. Avec une durée prolongée, ce tour vous permet de profiter des paysages variés du parc et de découvrir des trésors cachés.',
    de: 'Tauchen Sie ein in die ruhige Schönheit des Central Parks mit unserer Langen Tour, entworfen für alle, die jeden Moment in vollen Zügen genießen möchten. Mit einer längeren Dauer ermöglicht Ihnen diese Tour, die vielfältigen Landschaften des Parks zu genießen und versteckte Juwelen zu entdecken.',
    it: 'Immergiti nella tranquilla bellezza di Central Park con il nostro Tour Lungo, pensato per chi vuole assaporare ogni momento al massimo. Con una durata estesa, questo tour ti permette di goderti i diversi paesaggi del parco e scoprire gemme nascoste.',
    pt: 'Mergulhe na tranquila beleza do Central Park com nosso Tour Longo, projetado para quem quer saborear cada momento ao máximo. Com duração estendida, este tour permite que você desfrute das diversas paisagens do parque e descubra joias escondidas.',
    nl: 'Dompel jezelf onder in de rustige schoonheid van Central Park met onze Lange Tour, ontworpen voor wie elk moment ten volle wil proeven. Met een uitgebreide duur stelt deze tour je in staat de diverse landschappen van het park te genieten en verborgen pareltjes te ontdekken.',
    ru: 'Погрузитесь в безмятежную красоту Сентрал-парка с нашим Длинным туром, созданным для тех, кто хочет насладиться каждым моментом в полной мере. Увеличенная продолжительность позволит вам насладиться разнообразными ландшафтами парка и открыть скрытые жемчужины.',
    tr: 'Her anın tadını sonuna kadar çıkarmak isteyenler için tasarlanan Uzun Turumuzla Central Park\'ın huzurlu güzelliğine kendinizi bırakın. Uzun süresi sayesinde bu tur, parkın çeşitli manzaralarının tadını çıkarmanıza ve gizli cevherleri keşfetmenize olanak tanır.',
    ar: 'انغمس في الجمال الهادئ لسنترال بارك مع جولتنا الطويلة، المصممة لمن يريد التمتع بكل لحظة على أكمل وجه. بفضل المدة الممتدة، تتيح لك هذه الجولة الاستمتاع بالمناظر الطبيعية المتنوعة للحديقة واكتشاف كنوزها الخفية.',
    zh: '通过我们的长途之旅，沉浸在中央公园宁静的美景中，专为想要充分品味每一刻的人设计。延长的时间让您可以欣赏公园多样的景观并发现隐藏的瑰宝。',
    ja: 'すべての瞬間を心ゆくまで味わいたい方のために設計されたロングツアーで、セントラルパークの静かな美しさに浸ってください。延長された時間で、公園の多様な風景を楽しみ、隠れた宝石を発見できます。',
    ko: '롱 투어로 센트럴 파크의 고요한 아름다움에 빠져보세요. 모든 순간을 충분히 음미하고 싶은 분들을 위해 설계되었습니다. 연장된 시간으로 공원의 다양한 풍경을 즐기고 숨겨진 보석을 발견할 수 있습니다.',
    hi: 'हमारे लॉन्ग टूर के साथ सेंट्रल पार्क की शांत सुंदरता में डूब जाएं, उन लोगों के लिए डिज़ाइन किया गया जो हर पल का पूरा आनंद लेना चाहते हैं। विस्तारित अवधि के साथ, यह टूर आपको पार्क के विविध परिदृश्यों का आनंद लेने और छिपे हुए रत्नों की खोज करने देता है।',
  },
  'Embark on the ultimate Central Park adventure with our Grand Tour, featuring 2 carefully selected photo stops at the park\'s most iconic locations. This extended journey offers ample time to capture unforgettable memories and enjoy the park\'s grandeur.': {
    es: 'Embárcate en la aventura definitiva de Central Park con nuestro Grand Tour, que incluye 2 paradas fotográficas cuidadosamente seleccionadas en las ubicaciones más icónicas del parque. Este viaje extendido ofrece tiempo suficiente para capturar recuerdos inolvidables y disfrutar de la grandeza del parque.',
    fr: 'Embarquez pour l\'aventure ultime de Central Park avec notre Grand Tour, comprenant 2 arrêts photo soigneusement sélectionnés aux endroits les plus emblématiques du parc. Ce voyage prolongé offre amplement de temps pour capturer des souvenirs inoubliables et profiter de la grandeur du parc.',
    de: 'Begeben Sie sich auf das ultimative Central Park-Abenteuer mit unserer Grand Tour, die 2 sorgfältig ausgewählte Fotostopps an den ikonischsten Orten des Parks beinhaltet. Diese erweiterte Reise bietet reichlich Zeit, um unvergessliche Erinnerungen festzuhalten und die Pracht des Parks zu genießen.',
    it: 'Imbarcati nell\'ultima avventura di Central Park con il nostro Grand Tour, con 2 soste fotografiche accuratamente selezionate nelle località più iconiche del parco. Questo viaggio prolungato offre molto tempo per catturare ricordi indimenticabili e godere della grandezza del parco.',
    pt: 'Embarque na aventura definitiva no Central Park com nosso Grand Tour, com 2 paradas fotográficas cuidadosamente selecionadas nos locais mais icônicos do parque. Esta jornada estendida oferece tempo suficiente para capturar memórias inesquecíveis e desfrutar da grandeza do parque.',
    nl: 'Ga op het ultieme Central Park-avontuur met onze Grand Tour, met 2 zorgvuldig geselecteerde fotostops op de meest iconische locaties van het park. Deze uitgebreide reis biedt voldoende tijd om onvergetelijke herinneringen vast te leggen en de grandeur van het park te genieten.',
    ru: 'Отправьтесь в незабываемое приключение по Сентрал-парку с нашим Гранд туром, включающим 2 тщательно отобранные фотоостановки в самых культовых местах парка. Это продлённое путешествие даёт достаточно времени, чтобы запечатлеть незабываемые воспоминания и насладиться величием парка.',
    tr: 'Parkın en ikonik konumlarında özenle seçilmiş 2 fotoğraf duraklamasına sahip Grand Turumuzla nihai Central Park macerasına çıkın. Bu uzatılmış yolculuk, unutulmaz anılar yakalamak ve parkın ihtişamının tadını çıkarmak için bol zaman sunar.',
    ar: 'انطلق في مغامرة سنترال بارك القصوى مع جولتنا الكبرى، التي تضم محطتين تصوير منتقاتين بعناية في أكثر مواقع الحديقة شهرة. توفر هذه الرحلة الموسعة الوقت الكافي لالتقاط ذكريات لا تُنسى والاستمتاع بعظمة الحديقة.',
    zh: '通过我们的豪华之旅开启终极中央公园冒险，在公园最具标志性的地点精心挑选的 2 个拍照点。这段延长的旅程提供充足时间，让您捕捉难忘的回忆，享受公园的壮丽。',
    ja: '公園の最も象徴的な場所での慎重に選ばれた2つのフォトストップを特徴とするグランドツアーで、究極のセントラルパーク冒険に出かけましょう。この延長された旅は、忘れられない思い出を捉え、公園の壮大さを楽しむための十分な時間を提供します。',
    ko: '공원의 가장 상징적인 장소에서 신중하게 선택된 2개의 포토 스톱이 있는 그랜드 투어로 궁극의 센트럴 파크 모험을 떠나세요. 이 확장된 여정은 잊을 수 없는 추억을 담고 공원의 웅장함을 즐길 충분한 시간을 제공합니다.',
    hi: 'हमारे ग्रैंड टूर के साथ अंतिम सेंट्रल पार्क साहसिक यात्रा पर निकलें, जिसमें पार्क के सबसे प्रतिष्ठित स्थानों पर सावधानीपूर्वक चुने गए 2 फोटो स्टॉप शामिल हैं। यह विस्तारित यात्रा अविस्मरणीय यादें कैद करने और पार्क की भव्यता का आनंद लेने के लिए पर्याप्त समय प्रदान करती है।',
  },

  // ── Tour Section / Cards ─────────────────────────────────────────
  'Our Tours': {
    es: 'Nuestros Tours', fr: 'Nos Tours', de: 'Unsere Touren', it: 'I Nostri Tour', pt: 'Nossos Tours',
    nl: 'Onze Tours', ru: 'Наши Туры', tr: 'Turlarımız', ar: 'جولاتنا',
    zh: '我们的游览', ja: 'ツアー', ko: '우리의 투어', hi: 'हमारे टूर',
  },
  'Featured Tours': {
    es: 'Tours Destacados', fr: 'Tours Populaires', de: 'Beliebte Touren', it: 'Tour in Evidenza', pt: 'Tours Destacados',
    nl: 'Uitgelichte Tours', ru: 'Популярные Туры', tr: 'Öne Çıkan Turlar', ar: 'الجولات المميزة',
    zh: '精选游览', ja: 'おすすめツアー', ko: '추천 투어', hi: 'विशेष टूर',
  },
  'Choose Your Adventure': {
    es: 'Elige tu Aventura', fr: 'Choisissez votre Aventure', de: 'Wähle dein Abenteuer', it: 'Scegli la tua Avventura', pt: 'Escolha sua Aventura',
    nl: 'Kies je Avontuur', ru: 'Выберите Приключение', tr: 'Maceranızı Seçin', ar: 'اختر مغامرتك',
    zh: '选择您的冒险', ja: '冒険を選ぶ', ko: '모험 선택', hi: 'अपना साहसिक चुनें',
  },
  'minutes': {
    es: 'minutos', fr: 'minutes', de: 'Minuten', it: 'minuti', pt: 'minutos',
    nl: 'minuten', ru: 'минут', tr: 'dakika', ar: 'دقيقة',
    zh: '分钟', ja: '分', ko: '분', hi: 'मिनट',
  },
  'people': {
    es: 'personas', fr: 'personnes', de: 'Personen', it: 'persone', pt: 'pessoas',
    nl: 'personen', ru: 'человек', tr: 'kişi', ar: 'أشخاص',
    zh: '人', ja: '人', ko: '명', hi: 'लोग',
  },
  'Most Popular': {
    es: 'Más Popular', fr: 'Le Plus Populaire', de: 'Am Beliebtesten', it: 'Più Popolare', pt: 'Mais Popular',
    nl: 'Meest Populair', ru: 'Самый Популярный', tr: 'En Popüler', ar: 'الأكثر شعبية',
    zh: '最受欢迎', ja: '一番人気', ko: '가장 인기', hi: 'सबसे लोकप्रिय',
  },
  'Best Value': {
    es: 'Mejor Valor', fr: 'Meilleur Rapport', de: 'Bester Wert', it: 'Migliore Valore', pt: 'Melhor Custo',
    nl: 'Beste Waarde', ru: 'Лучшая Цена', tr: 'En İyi Değer', ar: 'أفضل قيمة',
    zh: '超值', ja: 'お買い得', ko: '최고 가치', hi: 'सर्वोत्तम मूल्य',
  },
  'New': {
    es: 'Nuevo', fr: 'Nouveau', de: 'Neu', it: 'Nuovo', pt: 'Novo',
    nl: 'Nieuw', ru: 'Новый', tr: 'Yeni', ar: 'جديد',
    zh: '新', ja: '新', ko: '신규', hi: 'नया',
  },
  'Learn More': {
    es: 'Saber Más', fr: 'En Savoir Plus', de: 'Mehr Erfahren', it: 'Scopri di Più', pt: 'Saiba Mais',
    nl: 'Meer Info', ru: 'Подробнее', tr: 'Daha Fazla', ar: 'اعرف المزيد',
    zh: '了解更多', ja: '詳細', ko: '더 알아보기', hi: 'और जानें',
  },
  'Read More': {
    es: 'Leer Más', fr: 'Lire Plus', de: 'Mehr Lesen', it: 'Leggi di Più', pt: 'Leia Mais',
    nl: 'Lees Meer', ru: 'Читать далее', tr: 'Devamını Oku', ar: 'اقرأ المزيد',
    zh: '阅读更多', ja: '続きを読む', ko: '더 보기', hi: 'और पढ़ें',
  },

  // ── Booking Modal — Steps & Fields ───────────────────────────────
  'Choose your tour': {
    es: 'Elige tu tour', fr: 'Choisissez votre tour', de: 'Wähle deine Tour', it: 'Scegli il tuo tour', pt: 'Escolha seu tour',
    nl: 'Kies je tour', ru: 'Выберите тур', tr: 'Turunuzu seçin', ar: 'اختر جولتك',
    zh: '选择游览', ja: 'ツアーを選ぶ', ko: '투어 선택', hi: 'अपना टूर चुनें',
  },
  'Your Details': {
    es: 'Tus Datos', fr: 'Vos Coordonnées', de: 'Ihre Daten', it: 'I Tuoi Dati', pt: 'Seus Dados',
    nl: 'Uw Gegevens', ru: 'Ваши Данные', tr: 'Bilgileriniz', ar: 'بياناتك',
    zh: '您的信息', ja: 'お客様情報', ko: '귀하의 정보', hi: 'आपकी जानकारी',
  },
  'Confirm & Pay': {
    es: 'Confirmar y Pagar', fr: 'Confirmer et Payer', de: 'Bestätigen & Bezahlen', it: 'Conferma e Paga', pt: 'Confirmar e Pagar',
    nl: 'Bevestig & Betaal', ru: 'Подтвердить и Оплатить', tr: 'Onayla ve Öde', ar: 'تأكيد ودفع',
    zh: '确认并支付', ja: '確認して支払う', ko: '확인 및 결제', hi: 'पुष्टि करें और भुगतान करें',
  },
  'Date': {
    es: 'Fecha', fr: 'Date', de: 'Datum', it: 'Data', pt: 'Data',
    nl: 'Datum', ru: 'Дата', tr: 'Tarih', ar: 'التاريخ',
    zh: '日期', ja: '日付', ko: '날짜', hi: 'तारीख',
  },
  'Time': {
    es: 'Hora', fr: 'Heure', de: 'Uhrzeit', it: 'Ora', pt: 'Hora',
    nl: 'Tijd', ru: 'Время', tr: 'Saat', ar: 'الوقت',
    zh: '时间', ja: '時間', ko: '시간', hi: 'समय',
  },
  'pedicabs': {
    es: 'pedicabs', fr: 'pédicabs', de: 'Pedicabs', it: 'pedicab', pt: 'pedicabs',
    nl: 'pedicabs', ru: 'педикэбы', tr: 'pedicablar', ar: 'مركبات',
    zh: '三轮车', ja: 'ペディキャブ', ko: '페디캡', hi: 'पेडीकैब',
  },
  'First Name': {
    es: 'Nombre', fr: 'Prénom', de: 'Vorname', it: 'Nome', pt: 'Nome',
    nl: 'Voornaam', ru: 'Имя', tr: 'Ad', ar: 'الاسم الأول',
    zh: '名', ja: '名', ko: '이름', hi: 'पहला नाम',
  },
  'Last Name': {
    es: 'Apellido', fr: 'Nom', de: 'Nachname', it: 'Cognome', pt: 'Sobrenome',
    nl: 'Achternaam', ru: 'Фамилия', tr: 'Soyad', ar: 'اسم العائلة',
    zh: '姓', ja: '姓', ko: '성', hi: 'अंतिम नाम',
  },
  'Email': {
    es: 'Correo electrónico', fr: 'E-mail', de: 'E-Mail', it: 'Email', pt: 'E-mail',
    nl: 'E-mail', ru: 'Эл. почта', tr: 'E-posta', ar: 'البريد الإلكتروني',
    zh: '电子邮件', ja: 'メール', ko: '이메일', hi: 'ईमेल',
  },
  'Phone': {
    es: 'Teléfono', fr: 'Téléphone', de: 'Telefon', it: 'Telefono', pt: 'Telefone',
    nl: 'Telefoon', ru: 'Телефон', tr: 'Telefon', ar: 'الهاتف',
    zh: '电话', ja: '電話', ko: '전화', hi: 'फ़ोन',
  },
  'Special Requests': {
    es: 'Solicitudes Especiales', fr: 'Demandes Spéciales', de: 'Besondere Wünsche', it: 'Richieste Speciali', pt: 'Pedidos Especiais',
    nl: 'Speciale Verzoeken', ru: 'Особые Пожелания', tr: 'Özel İstekler', ar: 'طلبات خاصة',
    zh: '特殊要求', ja: '特別なご要望', ko: '특별 요청', hi: 'विशेष अनुरोध',
  },
  'Special requests (optional)': {
    es: 'Solicitudes especiales (opcional)', fr: 'Demandes spéciales (facultatif)', de: 'Besondere Wünsche (optional)', it: 'Richieste speciali (opzionale)', pt: 'Pedidos especiais (opcional)',
    nl: 'Speciale verzoeken (optioneel)', ru: 'Особые пожелания (необязательно)', tr: 'Özel istekler (opsiyonel)', ar: 'طلبات خاصة (اختياري)',
    zh: '特殊要求 (可选)', ja: '特別なご要望 (任意)', ko: '특별 요청 (선택)', hi: 'विशेष अनुरोध (वैकल्पिक)',
  },
  'Complete Booking': {
    es: 'Completar Reserva', fr: 'Finaliser la Réservation', de: 'Buchung Abschließen', it: 'Completa Prenotazione', pt: 'Concluir Reserva',
    nl: 'Boeking Voltooien', ru: 'Завершить Бронирование', tr: 'Rezervasyonu Tamamla', ar: 'إكمال الحجز',
    zh: '完成预订', ja: '予約を完了', ko: '예약 완료', hi: 'बुकिंग पूरी करें',
  },
  'Continue': {
    es: 'Continuar', fr: 'Continuer', de: 'Weiter', it: 'Continua', pt: 'Continuar',
    nl: 'Doorgaan', ru: 'Продолжить', tr: 'Devam', ar: 'متابعة',
    zh: '继续', ja: '続ける', ko: '계속', hi: 'जारी रखें',
  },
  'Add to booking': {
    es: 'Añadir a la reserva', fr: 'Ajouter à la réservation', de: 'Zur Buchung hinzufügen', it: 'Aggiungi alla prenotazione', pt: 'Adicionar à reserva',
    nl: 'Aan boeking toevoegen', ru: 'Добавить к бронированию', tr: 'Rezervasyona ekle', ar: 'أضف إلى الحجز',
    zh: '添加到预订', ja: '予約に追加', ko: '예약에 추가', hi: 'बुकिंग में जोड़ें',
  },
  'Add-ons': {
    es: 'Complementos', fr: 'Extras', de: 'Zusätze', it: 'Extra', pt: 'Adicionais',
    nl: 'Extra\'s', ru: 'Дополнения', tr: 'Eklentiler', ar: 'الإضافات',
    zh: '附加项', ja: 'オプション', ko: '추가 옵션', hi: 'ऐड-ऑन',
  },
  'Adults': {
    es: 'Adultos', fr: 'Adultes', de: 'Erwachsene', it: 'Adulti', pt: 'Adultos',
    nl: 'Volwassenen', ru: 'Взрослые', tr: 'Yetişkinler', ar: 'البالغون',
    zh: '成人', ja: '大人', ko: '성인', hi: 'वयस्क',
  },
  'Children': {
    es: 'Niños', fr: 'Enfants', de: 'Kinder', it: 'Bambini', pt: 'Crianças',
    nl: 'Kinderen', ru: 'Дети', tr: 'Çocuklar', ar: 'الأطفال',
    zh: '儿童', ja: '子供', ko: '어린이', hi: 'बच्चे',
  },

  // ── Marketplace homepage / inline booking (HomeBooking, Hero, Stats) ──
  'Book a ride': {
    es: 'Reserva un paseo', fr: 'Réserver une balade', de: 'Fahrt buchen', it: 'Prenota un giro', pt: 'Reserve um passeio',
    nl: 'Boek een rit', ru: 'Забронировать поездку', tr: 'Tur ayırtın', ar: 'احجز جولة',
    zh: '预订行程', ja: '乗車を予約', ko: '탑승 예약', hi: 'सवारी बुक करें',
  },
  'Book your ride': {
    es: 'Reserva tu paseo', fr: 'Réservez votre balade', de: 'Buchen Sie Ihre Fahrt', it: 'Prenota il tuo giro', pt: 'Reserve seu passeio',
    nl: 'Boek je rit', ru: 'Забронируйте поездку', tr: 'Turunuzu ayırtın', ar: 'احجز جولتك',
    zh: '预订您的行程', ja: 'ご乗車を予約', ko: '탑승 예약하기', hi: 'अपनी सवारी बुक करें',
  },
  'Explore Central Park your way': {
    es: 'Explora Central Park a tu manera', fr: 'Explorez Central Park à votre façon', de: 'Erkunden Sie den Central Park auf Ihre Art', it: 'Esplora Central Park a modo tuo', pt: 'Explore o Central Park do seu jeito',
    nl: 'Ontdek Central Park op jouw manier', ru: 'Исследуйте Центральный парк по-своему', tr: 'Central Park’ı kendi tarzınızda keşfedin', ar: 'استكشف سنترال بارك بطريقتك',
    zh: '以您的方式探索中央公园', ja: 'あなたらしくセントラルパークを巡ろう', ko: '나만의 방식으로 센트럴 파크 즐기기', hi: 'सेंट्रल पार्क को अपने अंदाज़ में देखें',
  },
  'Book in 30 seconds': {
    es: 'Reserva en 30 segundos', fr: 'Réservez en 30 secondes', de: 'In 30 Sekunden buchen', it: 'Prenota in 30 secondi', pt: 'Reserve em 30 segundos',
    nl: 'Boek in 30 seconden', ru: 'Бронирование за 30 секунд', tr: '30 saniyede ayırtın', ar: 'احجز في 30 ثانية',
    zh: '30秒完成预订', ja: '30秒で予約', ko: '30초 만에 예약', hi: '30 सेकंड में बुक करें',
  },
  'Pedicab Tours': {
    es: 'Tours en pedicab', fr: 'Tours en pédicab', de: 'Pedicab-Touren', it: 'Tour in pedicab', pt: 'Tours de pedicab',
    nl: 'Pedicab-tours', ru: 'Туры на велорикше', tr: 'Pedikab turları', ar: 'جولات بيديكاب',
    zh: '人力车之旅', ja: 'ペディキャブツアー', ko: '페디캡 투어', hi: 'पेडीकैब टूर',
  },
  'Bike Tours': {
    es: 'Tours en bici', fr: 'Tours à vélo', de: 'Fahrradtouren', it: 'Tour in bici', pt: 'Tours de bicicleta',
    nl: 'Fietstours', ru: 'Велотуры', tr: 'Bisiklet turları', ar: 'جولات بالدراجة',
    zh: '自行车之旅', ja: '自転車ツアー', ko: '자전거 투어', hi: 'बाइक टूर',
  },
  'Bike Rentals': {
    es: 'Alquiler de bicis', fr: 'Location de vélos', de: 'Fahrradverleih', it: 'Noleggio bici', pt: 'Aluguel de bicicletas',
    nl: 'Fietsverhuur', ru: 'Аренда велосипедов', tr: 'Bisiklet kiralama', ar: 'تأجير الدراجات',
    zh: '自行车租赁', ja: '自転車レンタル', ko: '자전거 대여', hi: 'बाइक किराया',
  },
  'E-Scooter Tours': {
    es: 'Tours en patinete', fr: 'Tours en trottinette', de: 'E-Scooter-Touren', it: 'Tour in monopattino', pt: 'Tours de patinete',
    nl: 'E-scootertours', ru: 'Туры на электросамокате', tr: 'E-skuter turları', ar: 'جولات بالسكوتر الكهربائي',
    zh: '电动滑板车之旅', ja: '電動スクーターツアー', ko: '전동 스쿠터 투어', hi: 'ई-स्कूटर टूर',
  },
  'E-Scooter Rentals': {
    es: 'Alquiler de patinetes', fr: 'Location de trottinettes', de: 'E-Scooter-Verleih', it: 'Noleggio monopattini', pt: 'Aluguel de patinetes',
    nl: 'E-scooterverhuur', ru: 'Аренда электросамокатов', tr: 'E-skuter kiralama', ar: 'تأجير السكوترات الكهربائية',
    zh: '电动滑板车租赁', ja: '電動スクーターレンタル', ko: '전동 스쿠터 대여', hi: 'ई-स्कूटर किराया',
  },
  'Walking Tours': {
    es: 'Tours a pie', fr: 'Visites à pied', de: 'Geführte Spaziergänge', it: 'Tour a piedi', pt: 'Tours a pé',
    nl: 'Wandeltours', ru: 'Пешеходные туры', tr: 'Yürüyüş turları', ar: 'جولات سيرًا على الأقدام',
    zh: '徒步之旅', ja: 'ウォーキングツアー', ko: '도보 투어', hi: 'पैदल टूर',
  },
  'Running & Fitness': {
    es: 'Running y fitness', fr: 'Course & fitness', de: 'Laufen & Fitness', it: 'Corsa e fitness', pt: 'Corrida e fitness',
    nl: 'Hardlopen & fitness', ru: 'Бег и фитнес', tr: 'Koşu ve fitness', ar: 'الجري واللياقة',
    zh: '跑步与健身', ja: 'ランニング＆フィットネス', ko: '러닝 & 피트니스', hi: 'रनिंग और फिटनेस',
  },
  'Horse Carriage': {
    es: 'Carruaje de caballos', fr: 'Calèche', de: 'Pferdekutsche', it: 'Carrozza con cavalli', pt: 'Carruagem de cavalos',
    nl: 'Paardenkoets', ru: 'Конный экипаж', tr: 'Atlı araba', ar: 'عربة الخيل',
    zh: '马车', ja: '馬車', ko: '마차', hi: 'घोड़ा गाड़ी',
  },
  'What time?': {
    es: '¿A qué hora?', fr: 'À quelle heure ?', de: 'Um wie viel Uhr?', it: 'A che ora?', pt: 'A que horas?',
    nl: 'Hoe laat?', ru: 'Во сколько?', tr: 'Saat kaçta?', ar: 'في أي وقت؟',
    zh: '几点？', ja: '何時に？', ko: '몇 시에?', hi: 'किस समय?',
  },
  'How many guests?': {
    es: '¿Cuántos invitados?', fr: 'Combien d’invités ?', de: 'Wie viele Gäste?', it: 'Quanti ospiti?', pt: 'Quantos convidados?',
    nl: 'Hoeveel gasten?', ru: 'Сколько гостей?', tr: 'Kaç kişi?', ar: 'كم عدد الضيوف؟',
    zh: '几位客人？', ja: '何名様？', ko: '몇 분이신가요?', hi: 'कितने मेहमान?',
  },
  'Morning': {
    es: 'Mañana', fr: 'Matin', de: 'Vormittag', it: 'Mattina', pt: 'Manhã',
    nl: 'Ochtend', ru: 'Утро', tr: 'Sabah', ar: 'صباحًا',
    zh: '上午', ja: '午前', ko: '오전', hi: 'सुबह',
  },
  'Afternoon': {
    es: 'Tarde', fr: 'Après-midi', de: 'Nachmittag', it: 'Pomeriggio', pt: 'Tarde',
    nl: 'Middag', ru: 'День', tr: 'Öğleden sonra', ar: 'بعد الظهر',
    zh: '下午', ja: '午後', ko: '오후', hi: 'दोपहर',
  },
  'Evening': {
    es: 'Noche', fr: 'Soir', de: 'Abend', it: 'Sera', pt: 'Noite',
    nl: 'Avond', ru: 'Вечер', tr: 'Akşam', ar: 'مساءً',
    zh: '晚上', ja: '夕方', ko: '저녁', hi: 'शाम',
  },
  'Years on the Park': {
    es: 'Años en el parque', fr: 'Ans au parc', de: 'Jahre am Park', it: 'Anni nel parco', pt: 'Anos no parque',
    nl: 'Jaar in het park', ru: 'Лет в парке', tr: 'Yıldır parkta', ar: 'سنوات في الحديقة',
    zh: '深耕公园的年头', ja: '公園での年数', ko: '공원에서 보낸 세월', hi: 'पार्क में वर्षों से',
  },
  'Happy Guests': {
    es: 'Clientes felices', fr: 'Clients satisfaits', de: 'Zufriedene Gäste', it: 'Ospiti felici', pt: 'Clientes felizes',
    nl: 'Blije gasten', ru: 'Довольных гостей', tr: 'Mutlu misafir', ar: 'ضيوف سعداء',
    zh: '满意的客人', ja: '満足のお客様', ko: '행복한 손님', hi: 'खुश मेहमान',
  },
  'Ways to Ride': {
    es: 'Formas de pasear', fr: 'Façons de rouler', de: 'Arten zu fahren', it: 'Modi di girare', pt: 'Formas de passear',
    nl: 'Manieren om te rijden', ru: 'Способов прокатиться', tr: 'Gezme yolu', ar: 'طرق للتنقل',
    zh: '游览方式', ja: '楽しみ方', ko: '즐기는 방법', hi: 'सवारी के तरीके',
  },
  'Acres to Explore': {
    es: 'Acres por explorar', fr: 'Acres à explorer', de: 'Acres zum Erkunden', it: 'Acri da esplorare', pt: 'Acres para explorar',
    nl: 'Acres om te ontdekken', ru: 'Акров для прогулок', tr: 'Keşfedilecek dönüm', ar: 'فدان للاستكشاف',
    zh: '英亩待探索', ja: 'エーカーを探索', ko: '에이커를 탐험', hi: 'एकड़ का अन्वेषण',
  },
  'Required': {
    es: 'Obligatorio', fr: 'Obligatoire', de: 'Erforderlich', it: 'Obbligatorio', pt: 'Obrigatório',
    nl: 'Verplicht', ru: 'Обязательно', tr: 'Zorunlu', ar: 'مطلوب',
    zh: '必填', ja: '必須', ko: '필수', hi: 'आवश्यक',
  },

  // ── About / Marketing copy ───────────────────────────────────────
  'About Our Service': {
    es: 'Sobre Nuestro Servicio', fr: 'À Propos de Notre Service', de: 'Über Unseren Service', it: 'Il Nostro Servizio', pt: 'Sobre Nosso Serviço',
    nl: 'Over Onze Service', ru: 'О Нашем Сервисе', tr: 'Hizmetimiz Hakkında', ar: 'حول خدمتنا',
    zh: '关于我们的服务', ja: 'サービスについて', ko: '서비스 소개', hi: 'हमारी सेवा',
  },
  'Why Choose Us': {
    es: 'Por Qué Elegirnos', fr: 'Pourquoi Nous Choisir', de: 'Warum Wir', it: 'Perché Sceglierci', pt: 'Por Que Nos Escolher',
    nl: 'Waarom Wij', ru: 'Почему Мы', tr: 'Neden Biz', ar: 'لماذا تختارنا',
    zh: '为何选择我们', ja: '選ばれる理由', ko: '왜 우리인가', hi: 'हमें क्यों चुनें',
  },
  'Our Story': {
    es: 'Nuestra Historia', fr: 'Notre Histoire', de: 'Unsere Geschichte', it: 'La Nostra Storia', pt: 'Nossa História',
    nl: 'Ons Verhaal', ru: 'Наша История', tr: 'Hikayemiz', ar: 'قصتنا',
    zh: '我们的故事', ja: '私たちの物語', ko: '우리의 이야기', hi: 'हमारी कहानी',
  },
  'Trusted Since 2026': {
    es: 'De Confianza Desde 2026', fr: 'De Confiance Depuis 2026', de: 'Vertraut Seit 2026', it: 'Affidabile Dal 2026', pt: 'Confiável Desde 2026',
    nl: 'Vertrouwd Sinds 2026', ru: 'Доверяют С 2026', tr: '2026\'dan Beri Güvenilir', ar: 'موثوق منذ 2026',
    zh: '始于2026年值得信赖', ja: '2026年から信頼', ko: '2026년부터 신뢰', hi: '2026 से विश्वसनीय',
  },
  'Frequently Asked Questions': {
    es: 'Preguntas Frecuentes', fr: 'Questions Fréquentes', de: 'Häufige Fragen', it: 'Domande Frequenti', pt: 'Perguntas Frequentes',
    nl: 'Veelgestelde Vragen', ru: 'Часто Задаваемые Вопросы', tr: 'Sıkça Sorulan Sorular', ar: 'الأسئلة الشائعة',
    zh: '常见问题', ja: 'よくある質問', ko: '자주 묻는 질문', hi: 'अक्सर पूछे जाने वाले प्रश्न',
  },
  'Customer Reviews': {
    es: 'Reseñas de Clientes', fr: 'Avis Clients', de: 'Kundenbewertungen', it: 'Recensioni Clienti', pt: 'Avaliações de Clientes',
    nl: 'Klantbeoordelingen', ru: 'Отзывы Клиентов', tr: 'Müşteri Yorumları', ar: 'تقييمات العملاء',
    zh: '客户评价', ja: 'お客様の声', ko: '고객 리뷰', hi: 'ग्राहक समीक्षाएं',
  },
  'What Our Customers Say': {
    es: 'Lo Que Dicen Nuestros Clientes', fr: 'Ce Que Nos Clients Disent', de: 'Was Unsere Kunden Sagen', it: 'Cosa Dicono i Nostri Clienti', pt: 'O Que Dizem Nossos Clientes',
    nl: 'Wat Klanten Zeggen', ru: 'Что Говорят Наши Клиенты', tr: 'Müşterilerimiz Ne Diyor', ar: 'ماذا يقول عملاؤنا',
    zh: '客户怎么说', ja: 'お客様の声', ko: '고객의 말', hi: 'हमारे ग्राहक क्या कहते हैं',
  },
  'Send us a message': {
    es: 'Envíanos un mensaje', fr: 'Envoyez-nous un message', de: 'Senden Sie uns eine Nachricht', it: 'Inviaci un messaggio', pt: 'Envie-nos uma mensagem',
    nl: 'Stuur ons een bericht', ru: 'Отправьте нам сообщение', tr: 'Bize mesaj gönderin', ar: 'أرسل لنا رسالة',
    zh: '给我们留言', ja: 'メッセージを送る', ko: '메시지 보내기', hi: 'हमें संदेश भेजें',
  },
  'Message': {
    es: 'Mensaje', fr: 'Message', de: 'Nachricht', it: 'Messaggio', pt: 'Mensagem',
    nl: 'Bericht', ru: 'Сообщение', tr: 'Mesaj', ar: 'الرسالة',
    zh: '消息', ja: 'メッセージ', ko: '메시지', hi: 'संदेश',
  },
  'Send': {
    es: 'Enviar', fr: 'Envoyer', de: 'Senden', it: 'Invia', pt: 'Enviar',
    nl: 'Versturen', ru: 'Отправить', tr: 'Gönder', ar: 'إرسال',
    zh: '发送', ja: '送信', ko: '보내기', hi: 'भेजें',
  },
  'Subject': {
    es: 'Asunto', fr: 'Sujet', de: 'Betreff', it: 'Oggetto', pt: 'Assunto',
    nl: 'Onderwerp', ru: 'Тема', tr: 'Konu', ar: 'الموضوع',
    zh: '主题', ja: '件名', ko: '제목', hi: 'विषय',
  },
  'Address': {
    es: 'Dirección', fr: 'Adresse', de: 'Adresse', it: 'Indirizzo', pt: 'Endereço',
    nl: 'Adres', ru: 'Адрес', tr: 'Adres', ar: 'العنوان',
    zh: '地址', ja: '住所', ko: '주소', hi: 'पता',
  },
  'Get Directions': {
    es: 'Cómo Llegar', fr: 'Itinéraire', de: 'Wegbeschreibung', it: 'Indicazioni', pt: 'Como Chegar',
    nl: 'Routebeschrijving', ru: 'Маршрут', tr: 'Yol Tarifi', ar: 'الاتجاهات',
    zh: '获取路线', ja: '道順', ko: '길찾기', hi: 'दिशाएं प्राप्त करें',
  },

  // ── Footer headers + extra labels ────────────────────────────────
  'Quick Links': {
    es: 'Enlaces Rápidos', fr: 'Liens Rapides', de: 'Schnelllinks', it: 'Link Rapidi', pt: 'Links Rápidos',
    nl: 'Snelle Links', ru: 'Быстрые Ссылки', tr: 'Hızlı Bağlantılar', ar: 'روابط سريعة',
    zh: '快速链接', ja: 'クイックリンク', ko: '빠른 링크', hi: 'त्वरित लिंक',
  },
  'Our Services': {
    es: 'Nuestros Servicios', fr: 'Nos Services', de: 'Unsere Dienste', it: 'I Nostri Servizi', pt: 'Nossos Serviços',
    nl: 'Onze Diensten', ru: 'Наши Услуги', tr: 'Hizmetlerimiz', ar: 'خدماتنا',
    zh: '我们的服务', ja: 'サービス', ko: '서비스', hi: 'हमारी सेवाएं',
  },
  'Find Us': {
    es: 'Encuéntranos', fr: 'Nous Trouver', de: 'Finden Sie Uns', it: 'Trovaci', pt: 'Encontre-nos',
    nl: 'Vind Ons', ru: 'Найти Нас', tr: 'Bizi Bulun', ar: 'موقعنا',
    zh: '找到我们', ja: 'アクセス', ko: '찾아오시는 길', hi: 'हमें खोजें',
  },
  'Contact Us': {
    es: 'Contáctanos', fr: 'Nous Contacter', de: 'Kontaktiere Uns', it: 'Contattaci', pt: 'Fale Conosco',
    nl: 'Neem Contact Op', ru: 'Связаться с Нами', tr: 'Bize Ulaşın', ar: 'اتصل بنا',
    zh: '联系我们', ja: 'お問い合わせ', ko: '문의하기', hi: 'संपर्क करें',
  },
  'Pick-up Location': {
    es: 'Ubicación de Recogida', fr: 'Lieu de Prise en Charge', de: 'Abholort', it: 'Punto di Ritrovo', pt: 'Local de Embarque',
    nl: 'Ophaallocatie', ru: 'Место Встречи', tr: 'Buluşma Noktası', ar: 'موقع الالتقاء',
    zh: '上车地点', ja: '集合場所', ko: '픽업 장소', hi: 'पिक-अप स्थान',
  },
  'Express Tour': {
    es: 'Tour Express', fr: 'Tour Express', de: 'Express-Tour', it: 'Tour Express', pt: 'Tour Express',
    nl: 'Express Tour', ru: 'Экспресс Тур', tr: 'Ekspres Tur', ar: 'جولة سريعة',
    zh: '快速游', ja: '短時間ツアー', ko: '익스프레스 투어', hi: 'एक्सप्रेस टूर',
  },
  'Long Tour': {
    es: 'Tour Largo', fr: 'Tour Long', de: 'Lange Tour', it: 'Tour Lungo', pt: 'Tour Longo',
    nl: 'Lange Tour', ru: 'Длинный Тур', tr: 'Uzun Tur', ar: 'جولة طويلة',
    zh: '长游', ja: '長時間ツアー', ko: '롱 투어', hi: 'लंबा टूर',
  },
  'VIP Tour': {
    es: 'Tour VIP', fr: 'Tour VIP', de: 'VIP Tour', it: 'Tour VIP', pt: 'Tour VIP',
    nl: 'VIP Tour', ru: 'VIP Тур', tr: 'VIP Tur', ar: 'جولة VIP',
    zh: 'VIP游', ja: 'VIPツアー', ko: 'VIP 투어', hi: 'VIP टूर',
  },
  '💍 Proposal Packages': {
    es: '💍 Paquetes de Pedida', fr: '💍 Forfaits Demande', de: '💍 Antragspakete', it: '💍 Pacchetti Proposta', pt: '💍 Pacotes Pedido',
    nl: '💍 Aanzoek Pakketten', ru: '💍 Пакеты Предложений', tr: '💍 Evlilik Teklifi Paketleri', ar: '💍 باقات الخطوبة',
    zh: '💍 求婚套餐', ja: '💍 プロポーズパッケージ', ko: '💍 프러포즈 패키지', hi: '💍 प्रस्ताव पैकेज',
  },
  'NY Central Park Tour': {
    es: 'NY Central Park Tour', fr: 'NY Central Park Tour', de: 'NY Central Park Tour', it: 'NY Central Park Tour', pt: 'NY Central Park Tour',
    nl: 'NY Central Park Tour', ru: 'NY Central Park Tour', tr: 'NY Central Park Tour', ar: 'NY Central Park Tour',
    zh: 'NY Central Park Tour', ja: 'NY Central Park Tour', ko: 'NY Central Park Tour', hi: 'NY Central Park Tour',
  },
  '10K+ Reviews': {
    es: '10K+ Reseñas', fr: '10K+ Avis', de: '10K+ Bewertungen', it: '10K+ Recensioni', pt: '10K+ Avaliações',
    nl: '10K+ Beoordelingen', ru: '10K+ Отзывов', tr: '10K+ Yorum', ar: 'أكثر من 10 آلاف تقييم',
    zh: '10K+ 评价', ja: '10K+ レビュー', ko: '10K+ 리뷰', hi: '10K+ समीक्षाएं',
  },
  // Tours section / Tour cards (actual rendered text)
  'Duration': {
    es: 'Duración', fr: 'Durée', de: 'Dauer', it: 'Durata', pt: 'Duração',
    nl: 'Duur', ru: 'Длительность', tr: 'Süre', ar: 'المدة',
    zh: '时长', ja: '所要時間', ko: '소요 시간', hi: 'अवधि',
  },
  'Available Add-ons': {
    es: 'Complementos Disponibles', fr: 'Extras Disponibles', de: 'Verfügbare Extras', it: 'Extra Disponibili', pt: 'Adicionais Disponíveis',
    nl: 'Beschikbare Extra\'s', ru: 'Доступные Дополнения', tr: 'Mevcut Eklentiler', ar: 'الإضافات المتاحة',
    zh: '可选附加项', ja: '利用可能なオプション', ko: '이용 가능한 추가 옵션', hi: 'उपलब्ध ऐड-ऑन',
  },
  'Tour Route & Highlights': {
    es: 'Ruta y Atractivos del Tour', fr: 'Itinéraire et Points Forts', de: 'Tour-Route & Höhepunkte', it: 'Percorso e Punti Salienti', pt: 'Rota e Destaques do Tour',
    nl: 'Route & Hoogtepunten', ru: 'Маршрут и Достопримечательности', tr: 'Tur Rotası ve Önemli Noktalar', ar: 'مسار الجولة والمعالم',
    zh: '游览路线与亮点', ja: 'ツアールートとハイライト', ko: '투어 경로 및 하이라이트', hi: 'टूर मार्ग और मुख्य आकर्षण',
  },
  'About our family since 2026': {
    es: 'Sobre nuestra familia desde 2026', fr: 'Notre famille depuis 2026', de: 'Über unsere Familie seit 2026', it: 'La nostra famiglia dal 2026', pt: 'Sobre nossa família desde 2026',
    nl: 'Over onze familie sinds 2026', ru: 'О нашей семье с 2026', tr: '2026\'dan beri ailemiz hakkında', ar: 'عن عائلتنا منذ 2026',
    zh: '关于我们自2026年起的家族', ja: '2026年から続く家族について', ko: '2026년부터 우리 가족 소개', hi: '2026 से हमारे परिवार के बारे में',
  },
  'About our family': {
    es: 'Sobre nuestra familia', fr: 'Notre famille', de: 'Über unsere Familie', it: 'La nostra famiglia', pt: 'Sobre nossa família',
    nl: 'Over onze familie', ru: 'О нашей семье', tr: 'Ailemiz hakkında', ar: 'عن عائلتنا',
    zh: '关于我们的家族', ja: '私たちの家族について', ko: '우리 가족 소개', hi: 'हमारे परिवार के बारे में',
  },
  'Everything you need to know before your journey': {
    es: 'Todo lo que necesitas saber antes de tu viaje', fr: 'Tout ce que vous devez savoir avant votre voyage', de: 'Alles, was du vor deiner Reise wissen musst', it: 'Tutto quello che devi sapere prima del viaggio', pt: 'Tudo o que você precisa saber antes da viagem',
    nl: 'Alles wat je moet weten voor je reis', ru: 'Все, что нужно знать перед поездкой', tr: 'Yolculuğunuzdan önce bilmeniz gereken her şey', ar: 'كل ما تحتاج معرفته قبل رحلتك',
    zh: '旅行前您需要知道的一切', ja: '旅の前に知っておくべきこと', ko: '여행 전 알아야 할 모든 것', hi: 'अपनी यात्रा से पहले जानने योग्य सब कुछ',
  },
  'Explore individual tours': {
    es: 'Explora tours individuales', fr: 'Découvrez nos tours', de: 'Einzelne Touren erkunden', it: 'Esplora tour individuali', pt: 'Explore tours individuais',
    nl: 'Verken individuele tours', ru: 'Изучить отдельные туры', tr: 'Bireysel turları keşfedin', ar: 'استكشف الجولات الفردية',
    zh: '探索单独游览', ja: '個別ツアーを見る', ko: '개별 투어 보기', hi: 'अलग-अलग टूर देखें',
  },
  'Have questions? Read our FAQ': {
    es: '¿Preguntas? Lee nuestras FAQ', fr: 'Des questions ? Lisez nos FAQ', de: 'Fragen? Lesen Sie unsere FAQ', it: 'Domande? Leggi le FAQ', pt: 'Dúvidas? Leia nossas FAQ',
    nl: 'Vragen? Lees onze FAQ', ru: 'Вопросы? Прочтите FAQ', tr: 'Sorunuz var mı? SSS\'mizi okuyun', ar: 'هل لديك أسئلة؟ اقرأ الأسئلة الشائعة',
    zh: '有问题？阅读常见问题', ja: 'ご質問は？FAQをご覧ください', ko: '질문이 있으신가요? FAQ를 확인하세요', hi: 'प्रश्न हैं? हमारा FAQ पढ़ें',
  },
  'Planning something special? See our proposal packages': {
    es: '¿Planeas algo especial? Mira nuestros paquetes de pedida', fr: 'Vous planifiez quelque chose de spécial ? Voyez nos forfaits demande', de: 'Etwas Besonderes geplant? Sehen Sie unsere Antragspakete', it: 'Stai pianificando qualcosa di speciale? Guarda i pacchetti proposta', pt: 'Planejando algo especial? Veja nossos pacotes de pedido',
    nl: 'Iets speciaals plannen? Zie onze aanzoek pakketten', ru: 'Планируете что-то особенное? Посмотрите наши пакеты предложений', tr: 'Özel bir şey mi planlıyorsunuz? Evlilik teklifi paketlerimize bakın', ar: 'هل تخطط لشيء مميز؟ شاهد باقات الخطوبة',
    zh: '计划特别的事情？查看我们的求婚套餐', ja: '特別な計画はありますか？プロポーズパッケージをご覧ください', ko: '특별한 계획이 있나요? 프러포즈 패키지를 보세요', hi: 'कुछ खास योजना? हमारे प्रस्ताव पैकेज देखें',
  },
  'Loading booking form...': {
    es: 'Cargando formulario de reserva...', fr: 'Chargement du formulaire...', de: 'Buchungsformular wird geladen...', it: 'Caricamento del modulo...', pt: 'Carregando formulário...',
    nl: 'Boekingsformulier laden...', ru: 'Загрузка формы...', tr: 'Rezervasyon formu yükleniyor...', ar: 'جار تحميل النموذج...',
    zh: '加载预订表单...', ja: '予約フォームを読み込み中...', ko: '예약 양식 로딩 중...', hi: 'बुकिंग फ़ॉर्म लोड हो रहा है...',
  },

  // FAQ section
  'Common Questions': {
    es: 'Preguntas Comunes', fr: 'Questions Courantes', de: 'Häufige Fragen', it: 'Domande Comuni', pt: 'Perguntas Comuns',
    nl: 'Veelgestelde Vragen', ru: 'Общие Вопросы', tr: 'Sıkça Sorulan Sorular', ar: 'الأسئلة الشائعة',
    zh: '常见问题', ja: 'よくある質問', ko: '일반적인 질문', hi: 'सामान्य प्रश्न',
  },
  'Central Park pedicab Ride FAQ': {
    es: 'FAQ del Paseo en Carruaje por Central Park', fr: 'FAQ Promenade en Calèche Central Park', de: 'Central Park Kutschenfahrt FAQ', it: 'FAQ Giro in Carrozza a Central Park', pt: 'FAQ Passeio de Carruagem em Central Park',
    nl: 'Central Park Koetsenrit FAQ', ru: 'FAQ Поездки на Экипаже по Сентрал-Парку', tr: 'Central Park At Arabası Gezi SSS', ar: 'الأسئلة الشائعة عن جولة العربة في سنترال بارك',
    zh: '中央公园马车之旅常见问题', ja: 'セントラルパーク馬車ライドFAQ', ko: '센트럴 파크 마차 투어 FAQ', hi: 'सेंट्रल पार्क घोड़ा गाड़ी सवारी FAQ',
  },
  'No questions to display right now.': {
    es: 'No hay preguntas para mostrar ahora.', fr: 'Aucune question à afficher pour le moment.', de: 'Derzeit keine Fragen anzuzeigen.', it: 'Nessuna domanda da visualizzare ora.', pt: 'Nenhuma pergunta para exibir agora.',
    nl: 'Geen vragen om weer te geven.', ru: 'Сейчас нет вопросов для показа.', tr: 'Şu anda gösterilecek soru yok.', ar: 'لا توجد أسئلة لعرضها الآن.',
    zh: '目前没有问题显示。', ja: '現在表示する質問はありません。', ko: '현재 표시할 질문이 없습니다.', hi: 'अभी कोई प्रश्न प्रदर्शित नहीं है।',
  },

  // ── Common UI buttons / labels frequent across pages ─────────────
  'Submit': {
    es: 'Enviar', fr: 'Soumettre', de: 'Senden', it: 'Invia', pt: 'Enviar',
    nl: 'Verzenden', ru: 'Отправить', tr: 'Gönder', ar: 'إرسال',
    zh: '提交', ja: '送信', ko: '제출', hi: 'जमा करें',
  },
  'Save': {
    es: 'Guardar', fr: 'Enregistrer', de: 'Speichern', it: 'Salva', pt: 'Salvar',
    nl: 'Opslaan', ru: 'Сохранить', tr: 'Kaydet', ar: 'حفظ',
    zh: '保存', ja: '保存', ko: '저장', hi: 'सहेजें',
  },
  'Edit': {
    es: 'Editar', fr: 'Modifier', de: 'Bearbeiten', it: 'Modifica', pt: 'Editar',
    nl: 'Bewerken', ru: 'Изменить', tr: 'Düzenle', ar: 'تعديل',
    zh: '编辑', ja: '編集', ko: '편집', hi: 'संपादित',
  },
  'Delete': {
    es: 'Eliminar', fr: 'Supprimer', de: 'Löschen', it: 'Elimina', pt: 'Excluir',
    nl: 'Verwijderen', ru: 'Удалить', tr: 'Sil', ar: 'حذف',
    zh: '删除', ja: '削除', ko: '삭제', hi: 'हटाएं',
  },
  'Yes': {
    es: 'Sí', fr: 'Oui', de: 'Ja', it: 'Sì', pt: 'Sim',
    nl: 'Ja', ru: 'Да', tr: 'Evet', ar: 'نعم',
    zh: '是', ja: 'はい', ko: '예', hi: 'हाँ',
  },
  'No': {
    es: 'No', fr: 'Non', de: 'Nein', it: 'No', pt: 'Não',
    nl: 'Nee', ru: 'Нет', tr: 'Hayır', ar: 'لا',
    zh: '否', ja: 'いいえ', ko: '아니요', hi: 'नहीं',
  },
  'OK': {
    es: 'Aceptar', fr: 'OK', de: 'OK', it: 'OK', pt: 'OK',
    nl: 'OK', ru: 'OK', tr: 'Tamam', ar: 'حسناً',
    zh: '确定', ja: 'OK', ko: '확인', hi: 'ठीक है',
  },
  'View All': {
    es: 'Ver Todo', fr: 'Voir Tout', de: 'Alle Anzeigen', it: 'Vedi Tutto', pt: 'Ver Tudo',
    nl: 'Alles Bekijken', ru: 'Посмотреть Все', tr: 'Tümünü Gör', ar: 'عرض الكل',
    zh: '查看全部', ja: 'すべて表示', ko: '전체 보기', hi: 'सभी देखें',
  },
  'See More': {
    es: 'Ver Más', fr: 'Voir Plus', de: 'Mehr Anzeigen', it: 'Vedi di Più', pt: 'Ver Mais',
    nl: 'Meer Zien', ru: 'Показать Еще', tr: 'Daha Fazla Gör', ar: 'عرض المزيد',
    zh: '查看更多', ja: 'もっと見る', ko: '더 보기', hi: 'और देखें',
  },

  // Footer extras
  'per pedicab (up to 4 adults)': {
    es: 'por pedicab (hasta 4 adultos)', fr: 'par pédicab (jusqu\'à 4 adultes)', de: 'pro Pedicab (bis zu 4 Erwachsene)', it: 'per pedicab (fino a 4 adulti)', pt: 'por pedicab (até 4 adultos)',
    nl: 'per pedicab (tot 4 volwassenen)', ru: 'за педикэб (до 4 взрослых)', tr: 'pedicab başına (4 yetişkine kadar)', ar: 'لكل مركبة (حتى 4 بالغين)',
    zh: '每辆三轮车 (最多4成人)', ja: 'ペディキャブ1台あたり (大人4名まで)', ko: '페디캡당 (성인 4명까지)', hi: 'प्रति पेडीकैब (4 वयस्क तक)',
  },

  // ── Footer ───────────────────────────────────────────────────────
  'Privacy Policy': {
    es: 'Política de Privacidad', fr: 'Politique de Confidentialité', de: 'Datenschutzerklärung', it: 'Privacy', pt: 'Política de Privacidade',
    nl: 'Privacybeleid', ru: 'Конфиденциальность', tr: 'Gizlilik Politikası', ar: 'سياسة الخصوصية',
    zh: '隐私政策', ja: 'プライバシー', ko: '개인정보 처리방침', hi: 'गोपनीयता नीति',
  },
  'Terms of Service': {
    es: 'Términos de Servicio', fr: 'Conditions d\'Utilisation', de: 'Nutzungsbedingungen', it: 'Termini di Servizio', pt: 'Termos de Serviço',
    nl: 'Algemene Voorwaarden', ru: 'Условия использования', tr: 'Kullanım Şartları', ar: 'شروط الخدمة',
    zh: '服务条款', ja: '利用規約', ko: '이용 약관', hi: 'सेवा की शर्तें',
  },
  'Hours': {
    es: 'Horario', fr: 'Heures', de: 'Öffnungszeiten', it: 'Orari', pt: 'Horário',
    nl: 'Openingstijden', ru: 'Часы работы', tr: 'Çalışma Saatleri', ar: 'ساعات العمل',
    zh: '营业时间', ja: '営業時間', ko: '영업 시간', hi: 'समय',
  },
};

/**
 * Look up a translation. Returns the translated string for the active
 * language, or falls back to the original English text if no translation
 * exists for that language. The fallback ensures the UI never shows a
 * broken/empty string — at worst it shows English.
 */
export function translateText(englishText, lang) {
  if (!englishText || lang === 'en') return englishText;
  const entry = dictionary[englishText];
  if (!entry) return englishText;
  return entry[lang] || englishText;
}

/**
 * Translate a string that has an emoji prefix (like "💍 Proposal" or
 * "🎄 Christmas Tour") by stripping the emoji, translating the rest,
 * and re-attaching the emoji. This is how the menu items render —
 * the emoji is part of the source text but isn't in our dictionary.
 */
export function translateWithEmojiPrefix(text, lang) {
  if (!text || lang === 'en') return text;
  // Match leading emoji(s) followed by optional whitespace.
  const m = text.match(/^([^\p{L}\p{N}]+)\s*(.+)$/u);
  if (!m) return translateText(text, lang);
  const [, prefix, rest] = m;
  return `${prefix} ${translateText(rest.trim(), lang)}`;
}

export default dictionary;
